import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests if available
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token expiration
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: async (userData) => {
    const response = await apiClient.post('/auth/register', userData);
    return response.data;
  },

  login: async (credentials) => {
    const response = await apiClient.post('/auth/login', credentials);
    return response.data;
  },

  getProfile: async () => {
    const response = await apiClient.get('/user/profile');
    return response.data;
  },

  forgotPassword: async (email) => {
    const response = await apiClient.post('/auth/forgot-password', { email });
    return response.data;
  }
};

// Products API
export const productsAPI = {
  getAll: async () => {
    const response = await apiClient.get('/products');
    return response.data.products;
  },

  getById: async (id) => {
    const response = await apiClient.get(`/products/${id}`);
    return response.data;
  }
};

// Memberships API
export const membershipsAPI = {
  getAll: async () => {
    const response = await apiClient.get('/memberships');
    return response.data.memberships;
  },

  getById: async (id) => {
    const response = await apiClient.get(`/memberships/${id}`);
    return response.data;
  }
};

// Payments API
export const paymentsAPI = {
  createProductCheckout: async (productId, successUrl, cancelUrl, metadata = {}) => {
    // Real Stripe one-time payment via /api/bundles/checkout. The success_url /
    // cancel_url are derived server-side from `origin` so we just send origin.
    const origin = window.location.origin;
    const response = await apiClient.post('/bundles/checkout', {
      product_id: productId,
      origin,
    });
    return response.data;
  },

  getPurchaseStatus: async (sessionId) => {
    const response = await apiClient.get(`/bundles/purchase/${sessionId}`);
    return response.data;
  },

  createMembershipCheckout: async (membershipId, successUrl, cancelUrl, metadata = {}) => {
    const response = await apiClient.post('/payments-simple/checkout/membership', {
      membership_id: membershipId,
      success_url: successUrl,
      cancel_url: cancelUrl,
      metadata
    });
    return response.data;
  },

  getCheckoutStatus: async (sessionId) => {
    const response = await apiClient.get(`/payments/checkout/status/${sessionId}`);
    return response.data;
  }
};

// Contact API
export const contactAPI = {
  submit: async (contactData) => {
    const response = await apiClient.post('/contact/submit', contactData);
    return response.data;
  }
};


// Consultation API
export const consultationAPI = {
  submit: async (consultationData) => {
    const response = await apiClient.post('/consultation/submit', consultationData);
    return response.data;
  }
};


// User API
export const userAPI = {
  getPurchases: async () => {
    const response = await apiClient.get('/user/purchases');
    return response.data;
  }
};

// Utility functions
export const setAuthToken = (token) => {
  localStorage.setItem('access_token', token);
};

export const removeAuthToken = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('user');
};

export const getAuthToken = () => {
  return localStorage.getItem('access_token');
};

export const isAuthenticated = () => {
  return !!getAuthToken();
};